import { inject, Injectable } from '@angular/core';
import {
  ChartDatasetConfig,
  ChartDataService,
  ExtendedChartDataset,
} from '@app/core/services/charts/chart-data.service';
import { ChartData, ChartOptions, TooltipOptions } from 'chart.js';
import { _, TranslateService } from '@ngx-translate/core';
import { splitTextByLines } from '@app/shared/utils/split-label-by-words';
import { roundToTwoDecimals } from '@app/shared/utils/math';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';
import { ILocalizationKey } from '@app/core/interfaces/localization';
import { ChartColorType } from '@app/core/enums/chart-color-type.enum';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { FloatingPopupBarChartService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-bar-chart.service';
import { ICombinedSpreadChartData } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spread/interfaces/combined-spread-chart-data';

@Injectable()
export class FpLoggerNoiseSpreadChartService {
  protected floatingPopupBarChartService = inject(FloatingPopupBarChartService);
  protected chartDataService = inject(ChartDataService);
  protected translate: TranslateService = inject(TranslateService);
  protected colorManagementService = inject(ColorManagementService);
  protected formattingDataService = inject(FormattingDataService);

  private noDataLocalizationKey: ILocalizationKey = _(`@@shared-no-data-value-placeholder`);

  public generateOptions(): ChartOptions {
    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    return this.floatingPopupBarChartService.generateOptions({
      chartTranslation: {
        yLabel: _('@@chart-noise-spread__db'),
        xLabel: _('@@shared-date'),
        titleText: null,
      },
      beginAtZero: true,
      customTooltipConfig: tooltipOptions,
    });
  }

  public generateMinimizedOptions(): ChartOptions {
    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    return this.floatingPopupBarChartService.generateOptions({
      chartTranslation: {
        yLabel: _('@@chart-noise-spread__db'),
        xLabel: _('@@shared-date'),
        titleText: null,
      },
      beginAtZero: true,
      customTooltipConfig: tooltipOptions,
      interactionsEnabled: false,
    });
  }

  private dataConverter(data: ICombinedSpreadChartData): number | null {
    return roundToTwoDecimals(data.spread) ?? null;
  }

  private generateDatasetConfig(): ChartDatasetConfig<ICombinedSpreadChartData>[] {
    const getMainColor = (): tinycolor.Instance =>
      this.colorManagementService.getChartDefaultColor(ChartColorType.Primary);

    return [
      {
        label: '',
        tooltipLabel: '',
        type: 'bar',
        dataValue: (data: ICombinedSpreadChartData) => this.dataConverter(data),
        backgroundColor: () =>
          this.colorManagementService.getColorInstanceWithOpacity(getMainColor(), 0.6).toRgbString(),
        hoverBackgroundColor: () => getMainColor().toRgbString(),
        borderWidth: 0,
        barPercentage: 0.85,
        categoryPercentage: 1.0,
        minBarLength: 4,
      },
    ];
  }

  public getDataForChart(data: ICombinedSpreadChartData[]): ChartData {
    const datasetConfigs: ChartDatasetConfig<ICombinedSpreadChartData>[] = this.generateDatasetConfig();

    const labels = data.map(el => this.formattingDataService.formatDate(el.date));

    return {
      labels: labels.length ? labels : [this.translate.instant(this.noDataLocalizationKey)],
      datasets: this.chartDataService.generateDatasets<ICombinedSpreadChartData>([...data], datasetConfigs),
    };
  }

  public getMinimizedDataForChart(data: ICombinedSpreadChartData[]): ChartData {
    const datasetConfigs: ChartDatasetConfig<ICombinedSpreadChartData>[] = this.generateDatasetConfig();

    const labels = data.map(el => this.formattingDataService.formatDate(el.date));

    return {
      labels: labels.length ? labels : [this.translate.instant(this.noDataLocalizationKey)],
      datasets: this.chartDataService.generateDatasets<ICombinedSpreadChartData>([...data], datasetConfigs),
    };
  }

  private getTooltipOptions(): _DeepPartialObject<TooltipOptions> {
    return {
      callbacks: {
        title: () => {
          const text = this.translate.instant(_('@@shared-spread__text'));

          return splitTextByLines(text, 40);
        },
        label: tooltipItem => {
          const noDataText: string = this.translate.instant(this.noDataLocalizationKey);

          const dataset: ExtendedChartDataset<ICombinedSpreadChartData> = tooltipItem.dataset;

          const currentData: ICombinedSpreadChartData | null = dataset.meta
            ? dataset.meta[tooltipItem.dataIndex]
            : null;

          const value = currentData?.spread ?? null;

          const yFormattedValue =
            value !== null ? this.floatingPopupBarChartService.formatChartNumber(value, 0, 2) : null;

          const separator = '   ';

          return this.translate.instant(_('@@chart-tooltip-date-decibel'), {
            I18N_VAR_X_VALUE: tooltipItem.label ?? noDataText,
            I18N_VAR_SEPARATOR: separator,
            I18N_VAR_Y_VALUE: yFormattedValue ?? noDataText,
          });
        },
      },
    };
  }
}
